import React, { useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { AttendedEventMemberSummary } from '../../services/api';

export type AttendanceMapMode = 'clockIn' | 'clockOut';

interface SessionAttendeesMapProps {
  members: AttendedEventMemberSummary[];
  mode: AttendanceMapMode;
  sessionLatitude?: number | null;
  sessionLongitude?: number | null;
}

interface MapPoint {
  key: string;
  lat: number;
  lng: number;
  ceaNumber: string;
  name: string | null;
  timeScanned: string;
  isOutlier: boolean;
}

const SINGAPORE_CENTER: [number, number] = [1.3521, 103.8198];
const OUTLIER_DISTANCE_M = 50;
const SESSION_MARKER_COLOR = 'var(--color-info)';
const ATTENDEE_MARKER_COLOR = 'var(--color-danger)';

function isValidCoordinate(lat: number | null | undefined, lng: number | null | undefined): boolean {
  if (lat == null || lng == null) return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat === 0 && lng === 0) return false;
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

function haversineDistanceM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h =
    sinLat * sinLat +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * sinLng * sinLng;
  return 2 * R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-SG', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function createMarkerIcon(color: string, blink = false) {
  const blinkClass = blink ? ' session-attendee-marker-blink' : '';
  return L.divIcon({
    className: `custom-marker${blinkClass}`,
    html: `<div style="background-color: ${color}; width: 18px; height: 18px; border-radius: 50%; border: 2px solid var(--color-white); box-shadow: 0 2px 4px rgba(var(--color-shadow-rgb), 0.3);"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
}

const SessionAttendeesMap: React.FC<SessionAttendeesMapProps> = ({
  members,
  mode,
  sessionLatitude,
  sessionLongitude,
}) => {
  const mapRef = useRef<L.Map | null>(null);
  const attendeeMarkerIcon = useMemo(() => createMarkerIcon(ATTENDEE_MARKER_COLOR), []);
  const outlierMarkerIcon = useMemo(() => createMarkerIcon(ATTENDEE_MARKER_COLOR, true), []);
  const sessionMarkerIcon = useMemo(
    () =>
      L.divIcon({
        className: 'custom-marker session-host-marker',
        html: `<div style="background-color: ${SESSION_MARKER_COLOR}; width: 22px; height: 22px; border-radius: 50%; border: 3px solid var(--color-white); box-shadow: 0 2px 6px rgba(var(--color-shadow-rgb), 0.35);"></div>`,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      }),
    [],
  );

  const hasSessionLocation = isValidCoordinate(sessionLatitude, sessionLongitude);

  const points = useMemo<MapPoint[]>(() => {
    return members.flatMap((member) => {
      const lat = mode === 'clockIn' ? member.clockInLatitude : member.clockOutLatitude;
      const lng = mode === 'clockIn' ? member.clockInLongitude : member.clockOutLongitude;
      const timeScanned =
        mode === 'clockIn'
          ? member.clockedInAt || member.timeScanned
          : member.clockedOutAt;
      if (!isValidCoordinate(lat, lng) || !timeScanned) {
        return [];
      }
      const isOutlier =
        hasSessionLocation &&
        haversineDistanceM(sessionLatitude!, sessionLongitude!, lat!, lng!) > OUTLIER_DISTANCE_M;
      return [
        {
          key: `${member.ceaNumber}-${mode}`,
          lat: lat!,
          lng: lng!,
          ceaNumber: member.ceaNumber,
          name: member.name,
          timeScanned,
          isOutlier,
        },
      ];
    });
  }, [members, mode, hasSessionLocation, sessionLatitude, sessionLongitude]);

  const mapPositions = useMemo<[number, number][]>(() => {
    const positions = points.map((point) => [point.lat, point.lng] as [number, number]);
    if (hasSessionLocation) {
      positions.push([sessionLatitude!, sessionLongitude!]);
    }
    return positions;
  }, [points, hasSessionLocation, sessionLatitude, sessionLongitude]);

  const center = useMemo<[number, number]>(() => {
    if (mapPositions.length === 0) {
      return SINGAPORE_CENTER;
    }
    const latSum = mapPositions.reduce((sum, point) => sum + point[0], 0);
    const lngSum = mapPositions.reduce((sum, point) => sum + point[1], 0);
    return [latSum / mapPositions.length, lngSum / mapPositions.length];
  }, [mapPositions]);

  useEffect(() => {
    if (!mapRef.current) return;
    if (mapPositions.length === 0) {
      mapRef.current.setView(SINGAPORE_CENTER, 11);
      return;
    }
    if (mapPositions.length === 1) {
      mapRef.current.setView(mapPositions[0], 16);
      return;
    }
    const bounds = L.latLngBounds(mapPositions);
    mapRef.current.fitBounds(bounds, { padding: [32, 32], maxZoom: 16 });
  }, [mapPositions]);

  const MapController = () => {
    const map = useMap();
    useEffect(() => {
      mapRef.current = map;
    }, [map]);
    return null;
  };

  return (
    <>
      <style>{`
        @keyframes session-attendee-marker-blink {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.35; transform: scale(1.2); }
        }
        .session-attendee-marker-blink > div {
          animation: session-attendee-marker-blink 1s ease-in-out infinite;
        }
      `}</style>
      <MapContainer
        center={center}
        zoom={mapPositions.length === 0 ? 11 : 14}
        style={{ height: '100%', width: '100%', minHeight: 280 }}
      >
        <MapController />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {hasSessionLocation && (
          <Marker
            key="session-host-location"
            position={[sessionLatitude!, sessionLongitude!]}
            icon={sessionMarkerIcon}
            zIndexOffset={1000}
          >
            <Popup>
              <strong>Session QR location</strong>
              <br />
              Recorded when clock-in/out QR was shown on the session day.
            </Popup>
          </Marker>
        )}
        {points.map((point) => (
          <Marker
            key={point.key}
            position={[point.lat, point.lng]}
            icon={point.isOutlier ? outlierMarkerIcon : attendeeMarkerIcon}
          >
            <Popup>
              <strong>{point.name || '—'}</strong>
              <br />
              CEA: {point.ceaNumber || '—'}
              <br />
              Scanned: {formatTimestamp(point.timeScanned)}
              {point.isOutlier && (
                <>
                  <br />
                  <strong>Outlier:</strong> more than {OUTLIER_DISTANCE_M}m from session QR location
                </>
              )}
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </>
  );
};

export default SessionAttendeesMap;
